import {AuthHeaders} from 'app/hooks/api/headers'
import useGetReverseGeocodingMapBox from 'app/hooks/external/useGetReverseGeocodingMapbox'
import {CUser} from 'app/modules/discovery/core'
import axios, {AxiosRequestConfig} from 'axios'
import {IUserFE} from 'interfaces/IUserFE'
import _ from 'lodash'
import React, {ReactNode, createContext, useCallback, useEffect, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {fetchUser} from 'redux/actions/userActions'
import {selectUser} from 'redux/selectors/userSelector'
import {AppDispatch} from 'redux/store'
import {emGetTenants} from 'utilities/emUserFeatures'
import {JsonObject} from 'utilities/types'

export const LOCAL_CURRENT_TENANT_ID = 'selected_tenant_unique_id'
export const LOCAL_CURRENT_TENANT_NAME = 'selected_tenant_name'

interface AuthContextState {
  currentUser: CUser | undefined
  reduxUser: IUserFE | undefined
  setCurrentUser: (user: CUser | undefined) => void
  getAuthHeaders: () => AuthHeaders
  handleTenantChange: (tenant_id: string, callbackFn: () => void) => void
}

const initAuthContextPropsState = {
  currentUser: undefined,
  reduxUser: undefined,
  setCurrentUser: () => {},
  getAuthHeaders: () => ({}),
  handleTenantChange: () => {},
}

const AuthContext = createContext<AuthContextState>(initAuthContextPropsState)

export default function AuthProvider({children}: {children: ReactNode}) {
  const userLocationAddressHk = useGetReverseGeocodingMapBox()
  const dispatch = useDispatch<AppDispatch>()
  const userSelf = useSelector(selectUser)
  //const userSelf = useGetUserQuery()
  const [reduxUser, setReduxUser] = useState(userSelf)
  const [currentUser, setStateCurrentUser] = useState<CUser | undefined>(
    userSelf ? new CUser(userSelf as unknown as CUser & JsonObject) : undefined
  )
  const [showSplashScreen, setShowSplashScreen] = useState(true)

  //TODO-MARK - need to send request to cognitio to expire token, this sets/unsets localStorage item that is not being used
  // const saveAuth = (auth: AuthModel | undefined) => {
  //   setAuth(auth)
  //   if (auth) {
  //     authHelper.setAuth(auth)
  //   } else {
  //     authHelper.removeAuth()
  //   }
  // }

  const setCurrentUser = (user: CUser | undefined) => {
    setStateCurrentUser(user)
  }

  const getAuthHeaders = useCallback(() => {
    return {'x-tenant-unique-id': `${localStorage.getItem(LOCAL_CURRENT_TENANT_ID)}`}
  }, [currentUser])

  const handleTenantChange = useCallback(
    (tenant_id: string, callbackFn: () => void) => {
      localStorage.setItem(LOCAL_CURRENT_TENANT_ID, tenant_id)
      const tenantName = reduxUser
        ? emGetTenants(reduxUser)?.find((f) => f.id === tenant_id)?.name ?? ''
        : ''
      localStorage.setItem(LOCAL_CURRENT_TENANT_NAME, tenantName)
      callbackFn()
    },
    [reduxUser]
  )

  useEffect(() => {
    if (currentUser && showSplashScreen) {
      if (currentUser) {
        if (currentUser?.activeTenantData?.id) {
          axios.interceptors.request.use(
            async (config: AxiosRequestConfig): Promise<AxiosRequestConfig> => {
              // To prevent tenant ID being passed to a request, include false for x-tenant-unique-id.
              if (!config.headers) {
                config.headers = {}
              }
              if (config.headers['x-tenant-unique-id'] === undefined) {
                console.log('setting the headers in axios now')
                config.headers = {
                  ...config.headers,
                  'x-tenant-unique-id': currentUser.activeTenantData?.id ?? '',
                }
              } else if (config.headers['x-tenant-unique-id'] === false) {
                console.log('some kind of weird setting x-tenant-id to false')
                delete config.headers['x-tenant-unique-id']
              }

              return config
            }
          )
          setShowSplashScreen(false)
        }
      }
      //o set location for user
      //Removing this if we have a location already
      if (!currentUser?.profile?.iscout_accessed_locations?.length) {
        try {
          navigator.geolocation.getCurrentPosition(function (position) {
            const latitude = position.coords.latitude
            const longitude = position.coords.longitude
            userLocationAddressHk
              .mutateAsync({
                longitude: position.coords.longitude,
                latitude: position.coords.latitude,
              })
              .then((res) => {
                const place = res.length ? res.filter((f: any) => f.id.includes('place')) : []
                const placeName = place.length ? place[0]?.place_name : ''
                const placeArr = placeName.split(',')
                const poi = res.length ? res.filter((f: any) => f.id.includes('poi')) : []
                place.length &&
                  !currentUser?.profile.iscout_accessed_locations?.find(
                    (f) => f.latitude === latitude && f.longitude === longitude
                  ) &&
                  currentUser?.profile.iscout_accessed_locations?.push({
                    longitude: longitude,
                    latitude: latitude,
                    addressLong: place[0]?.place_name,
                    poi: poi.length ? poi[0].place_name : '',
                    city: placeArr.length ? placeArr[0]?.trim() : '',
                    state: placeArr.length && placeArr.length > 1 ? placeArr[1]?.trim() : '',
                    country: placeArr.length && placeArr.length > 2 ? placeArr[2]?.trim() : '',
                    accessDate: new Date(),
                  })
              })
          })
        } catch (e) {
          console.log('error obtaining location: error:' + e)
        }
      }
    } else {
      //Anytime the user is updated in redux store, we want to re-assign the user so all dependencies are triggered to updated
      //currentUser is triggered if reduxUser is updated because it uses the reduxUser from selector in userSelf. so n dependency is needed to trigger currentUser
      //GOAL would be to not use currentUser and just use reduxUser, but there are some components that rely on currentUser, so we have to keep it
      //that rely on currentUser, we set when the objects are not equal and use lodash for a depp comparison (compares fields, keys, values, as they are nested)
      if (
        userSelf &&
        !_.isEqual(
          userSelf ? new CUser(userSelf as unknown as CUser & JsonObject) : undefined,
          currentUser
        )
      ) {
        setCurrentUser(userSelf ? new CUser(userSelf as unknown as CUser & JsonObject) : undefined)
      }
      if (userSelf && !_.isEqual(reduxUser, userSelf)) {
        setReduxUser(userSelf)
      }

      //when not using RTK-Query
      if (!userSelf) {
        dispatch(fetchUser())
      }
    }
  }, [userSelf, reduxUser])

  return (
    <AuthContext.Provider
      value={{currentUser, reduxUser, setCurrentUser, getAuthHeaders, handleTenantChange}}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuthProvider = () => React.useContext(AuthContext)
